/**
 * Persists purchase items as separate purchase-item records
 * (the purchase collection links them via the purchaseItems relation).
 */

import { purchaseItemsService } from '../../services';
import { PurchaseItem } from '../../types';

const unwrap = (response: any) => response?.data ?? response;

const toItemPayload = (item: PurchaseItem) => ({
  name: item.name || 'Purchase Item',
  quantity: Number(item.quantity) || 1,
  unitPrice: Number(item.unitPrice) || 0,
  total: Number(item.total) || 0,
});

/**
 * Creates/updates purchase-item records and returns their documentIds
 * so they can be linked on the purchase via purchaseItems.
 */
export async function savePurchaseItems(items: PurchaseItem[]): Promise<string[]> {
  const documentIds: string[] = [];
  for (const item of items) {
    const payload = toItemPayload(item);
    if (item.documentId) {
      await purchaseItemsService.update(item.documentId as any, { ...payload, documentId: item.documentId });
      documentIds.push(item.documentId);
    } else {
      const created = unwrap(await purchaseItemsService.create(payload));
      if (created?.documentId) documentIds.push(created.documentId);
    }
  }
  return documentIds;
}

/** Deletes item records that were removed from the form during an edit */
export async function deleteRemovedPurchaseItems(
  previousItems: PurchaseItem[] | undefined,
  keptDocumentIds: string[]
): Promise<void> {
  const removed = (previousItems || [])
    .filter((item) => item.documentId && !keptDocumentIds.includes(item.documentId))
    .map((item) => item.documentId as string);
  for (const documentId of removed) {
    try {
      await purchaseItemsService.delete(documentId);
    } catch (error) {
      console.error('Failed to delete removed purchase item:', documentId, error);
    }
  }
}

/** Loads the persisted items of a purchase for the edit form */
export async function fetchPurchaseItems(purchaseDocumentId: string): Promise<PurchaseItem[]> {
  try {
    const response: any = await purchaseItemsService.getAll({
      filters: { purchase: { documentId: { $eq: purchaseDocumentId } } },
      pagination: { pageSize: 200 },
    });
    const rows = response?.data || [];
    return rows.map((row: any) => ({
      documentId: row.documentId,
      id: String(row.id ?? row.documentId),
      name: row.name || '',
      quantity: Number(row.quantity) || 0,
      unitPrice: Number(row.unitPrice) || 0,
      total: Number(row.total) || 0,
    }));
  } catch (error) {
    console.error('Failed to load purchase items:', error);
    return [];
  }
}
